import { camelCase } from 'lodash-es';

import {
  PICKLIST_FILTER_MAP,
  DATE_RANGE_FILTER_MAP,
  NUMERIC_RANGE_FILTER_MAP,
  mapUserFiltersToFragment,
} from '../build-tc-wells-query-payload';
import type {
  FilterCategory,
  TcWellFilters,
  TcWellsQueryPayload,
} from '../types';
import type { SavedWellFilter } from 'types/type-curve-workspace-show';

/**
 * Round-trip helpers for a saved wells filter's `definition` jsonb.
 *
 * The filter is stored as the mapped wells-query fragment (camelCase keys such
 * as `holeDirectionIds`, `permitDate: {start,end}`, `distanceToTopFt:
 * {min,max}`). On WRITE the backend deep-underscores the keys; on READ jbuilder
 * camelizes only the top-level response keys, so the nested `definition` keys
 * come back snake_case. Every read path must therefore {@link camelizeDeep}
 * before interpreting a definition.
 */

// Recursively camelCase object keys; arrays/primitives (and string values) are untouched.
export function camelizeDeep(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(camelizeDeep);
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      out[camelCase(key)] = camelizeDeep(val);
    }
    return out;
  }
  return value;
}

// Build a filter `definition` from the modal's flat filter selections.
export function buildWellFilterDefinition(
  filters: TcWellFilters
): Record<string, unknown> {
  return mapUserFiltersToFragment(filters) as Record<string, unknown>;
}

// Normalize a stored definition into a payload fragment ready to merge into the
// wells query. Just camelizes the (snake_case-on-read) keys — values are
// already in payload shape.
export function parseDefinitionToFragment(
  definition: Record<string, unknown>
): Partial<TcWellsQueryPayload> {
  return camelizeDeep(definition ?? {}) as Partial<TcWellsQueryPayload>;
}

// Reverse lookups: payloadKey -> filterKey, derived from the forward maps so
// there is a single source of truth for the mapping.
const PICKLIST_REVERSE = invert(PICKLIST_FILTER_MAP);
const DATE_RANGE_REVERSE = invert(DATE_RANGE_FILTER_MAP);
const NUMERIC_RANGE_REVERSE = invert(NUMERIC_RANGE_FILTER_MAP);

function invert(map: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [filterKey, payloadKey] of Object.entries(map)) {
    out[payloadKey] = filterKey;
  }
  return out;
}

// Invert a stored definition back into the modal's flat `TcWellFilters` record
// so an existing filter can be edited. Handles the snake_case-on-read case by
// camelizing first.
export function definitionToTcWellFilters(
  definition: Record<string, unknown>
): TcWellFilters {
  const fragment = camelizeDeep(definition ?? {}) as Record<string, unknown>;
  const filters: TcWellFilters = {};

  for (const [payloadKey, value] of Object.entries(fragment)) {
    if (payloadKey in PICKLIST_REVERSE) {
      if (Array.isArray(value)) {
        filters[PICKLIST_REVERSE[payloadKey]] = value as string[];
      }
      continue;
    }

    if (payloadKey in DATE_RANGE_REVERSE) {
      const filterKey = DATE_RANGE_REVERSE[payloadKey];
      const range = (value ?? {}) as { start?: string; end?: string };
      if (range.start !== undefined) filters[`min_${filterKey}`] = range.start;
      if (range.end !== undefined) filters[`max_${filterKey}`] = range.end;
      continue;
    }

    if (payloadKey in NUMERIC_RANGE_REVERSE) {
      const filterKey = NUMERIC_RANGE_REVERSE[payloadKey];
      const range = (value ?? {}) as { min?: number; max?: number };
      if (range.min !== undefined) filters[`min_${filterKey}`] = range.min;
      if (range.max !== undefined) filters[`max_${filterKey}`] = range.max;
    }
  }

  return filters;
}

// The flat-record keys belonging to ONE category: picklist → `param`;
// range → `min_<param>`/`max_<param>`. Returns only the keys that are set.
export function sliceForCategory(
  cat: FilterCategory,
  filters: TcWellFilters
): TcWellFilters {
  const slice: TcWellFilters = {};
  if (cat.type === 'picklist') {
    const value = filters[cat.param];
    if (Array.isArray(value) && value.length > 0) slice[cat.param] = value;
  } else {
    if (cat.minParam && filters[cat.minParam] !== undefined) {
      slice[cat.minParam] = filters[cat.minParam];
    }
    if (cat.maxParam && filters[cat.maxParam] !== undefined) {
      slice[cat.maxParam] = filters[cat.maxParam];
    }
  }
  return slice;
}

// A single-property `definition` for one category (empty object when unset).
export function categoryDefinition(
  cat: FilterCategory,
  filters: TcWellFilters
): Record<string, unknown> {
  return buildWellFilterDefinition(sliceForCategory(cat, filters));
}

// Merge saved rows' definitions into one flat `TcWellFilters` record to hydrate
// the applied filters on load.
export function rowsToFlat(rows: SavedWellFilter[]): TcWellFilters {
  const flat: TcWellFilters = {};
  for (const row of rows) {
    // Only 'filter' rows are wells filters — cross_plot and color rows share the
    // analog_well_filter table but their definitions are not wells filters.
    if (row.kind !== 'filter') continue;
    Object.assign(flat, definitionToTcWellFilters(row.definition));
  }
  return flat;
}
